import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {
    MxAuthenticationService,
    MxNotificationService,
    MxPreloaderService,
    MxPreviousRouteService,
    MxRecentlyUsedService
} from 'mx-core';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {catchError, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {PrlInitializeService} from './prl-initialize.service';
import {
    PrlTermsAndConditionsPageService
} from '../../view/prl-authentication/services/terms-and-conditions-page/prl-terms-and-conditions-page.service';
import {PrlPagesService} from './prl-pages.service';

const TOKEN = 'X-Auth-token';
const FORCE_RESET_MARKER = 'force_reset';
const FORCE_RESET_RESPONSE_PREFIX = FORCE_RESET_MARKER + ',';
const FORCE_RESET_REQUIRED_KEY = 'forceResetRequired';
const TOKEN_COOKIE_PREFIX = 'token=';
const REMOVED_TOKEN_COOKIE = 'token=; Max-Age=0; path=/';

@Injectable({
    providedIn: 'root'
})
export class PrlAuthenticationService implements OnDestroy, MxAuthenticationService {

    private loginFailed = 'label.login.failed';
    private _authentication$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    private _destroy$$: Subject<null> = new Subject<null>();
    private _token: string = localStorage.getItem(TOKEN);

    constructor(
        private router: Router,
        private previous: MxPreviousRouteService,
        private preloader: MxPreloaderService,
        private notification: MxNotificationService,
        private initializeService: PrlInitializeService,
        private prlTermsAndConditionsPageService: PrlTermsAndConditionsPageService,
        private recentlyUsed: MxRecentlyUsedService,
        private prlPagesService: PrlPagesService,
        private httpClient: HttpClient,
        private dialogRef: MatDialog) {
    }

    public getAuthenticationObsr(): Observable<string | null> {
        return this._authentication$.pipe(
            takeUntil(this._destroy$$)
        );
    }

    public getAuthenticatedUserName(): string {
        return this._authentication$.value;
    }

    public get token(): string {
        return this._token;
    }

    public set token(token: string) {
        if (token) {
            localStorage.setItem(TOKEN, token);
        } else {
            localStorage.removeItem(TOKEN);
            localStorage.removeItem('SSO');
        }
        this._token = token;
    }

    public isAuthTokenChanged(): boolean {
        return !!this._token && this._token !== localStorage.getItem(TOKEN);
    }

    public handleLoginFromBo(): void {
        const allCookies = document.cookie.split('; ');
        const tokenCookie = allCookies.find(cookie => cookie.startsWith(TOKEN_COOKIE_PREFIX));

        if (!tokenCookie) {
            return;
        }

        this.token = tokenCookie.replace(TOKEN_COOKIE_PREFIX, '');
        document.cookie = REMOVED_TOKEN_COOKIE;
        localStorage.removeItem('isTcAccepted');

        this.prlTermsAndConditionsPageService.setIsTcAccepted().subscribe(isTcAccepted => {
            if (isTcAccepted) {
                this.router.navigateByUrl('/dashboard');
            }
        });
    }

    public authenticate(authParams: { username: string, password: string, 'remember-me': string }): void {
        this.preloader.show();

        let basicHeader;
        try {
            basicHeader = btoa(authParams.username + ':' + authParams.password);
        } catch (e) {
            this.invalidateAuth(this.loginFailed);
            return;
        }
        this.getRequest(this.httpClient.get('/rest/login',
            {
                headers: new HttpHeaders({
                    'Authorization': 'Basic ' + basicHeader
                }),
                params: {'rememberMe': authParams['remember-me']},
                responseType: 'text'
            })
        ).subscribe(auth => {
            if (auth?.startsWith(FORCE_RESET_RESPONSE_PREFIX)) {
                const token = auth.substring(FORCE_RESET_RESPONSE_PREFIX.length);
                if (token) {
                    this.token = token;
                }
                this.setForceResetRequired(true);
                this.router.navigateByUrl('/force-reset-password');
                return;
            }

            if (auth === FORCE_RESET_MARKER) {
                this.setForceResetRequired(true);
                this.router.navigateByUrl('/force-reset-password');
                return;
            }

            this.setForceResetRequired(false);
            if (auth) {
                this.token = auth;
                this.prlTermsAndConditionsPageService.setIsTcAccepted().subscribe(isTcAccepted => {
                    sessionStorage.clear();
                    if (isTcAccepted) {
                        if (!this.previous.getPreviousUrl()?.includes('/reset-password/')) {
                            this.previous.navigatePreviousPage();
                        } else {
                            this.router.navigateByUrl('/');
                        }
                    } else {
                        this.router.navigateByUrl('/terms');
                    }

                    this.recentlyUsed.clear();
                });
            } else {
                this._authentication$.next(null);
            }
        }).add(() => this.preloader.hide());
    }

    public checkAuth(): Observable<boolean> {
        return this.getRequest(this.httpClient.get('/rest/account/logged-user', {responseType: 'text'}))
            .pipe(
                switchMap(auth => {
                    if (auth?.startsWith(FORCE_RESET_RESPONSE_PREFIX)) {
                        const token = auth.substring(FORCE_RESET_RESPONSE_PREFIX.length);
                        if (token) {
                            this.token = token;
                        }
                        this.setForceResetRequired(true);
                        this.router.navigateByUrl('/force-reset-password');
                        return of(false);
                    }

                    if (auth === FORCE_RESET_MARKER) {
                        this.setForceResetRequired(true);
                        this.router.navigateByUrl('/force-reset-password');
                        return of(false);
                    }

                    if (auth) {
                        this._authentication$.next(auth);
                        return this.httpClient.get<boolean>('/rest/account/password/force-reset-required').pipe(
                            map(required => {
                                if (required) {
                                    this.setForceResetRequired(true);
                                    this.router.navigateByUrl('/force-reset-password');
                                    return false;
                                }
                                this.setForceResetRequired(false);
                                return true;
                            }),
                            catchError((error: HttpErrorResponse) => {
                                if (error.status === 401) {
                                    this.invalidateAuth(null);
                                }
                                return of(false);
                            })
                        );
                    }

                    this._authentication$.next(null);
                    this.invalidateAuth(null);
                    return of(false);
                })
            );
    }

    public completeForceResetLogin(): Observable<boolean> {
        this.clearForceResetRequired();

        return this.checkAuth().pipe(
            switchMap(authenticated => {
                if (!authenticated) {
                    return of(false);
                }

                return this.prlTermsAndConditionsPageService.setIsTcAccepted().pipe(
                    map(isTcAccepted => {
                        sessionStorage.clear();
                        this.recentlyUsed.clear();
                        this.router.navigateByUrl(isTcAccepted ? '/dashboard' : '/terms');
                        return true;
                    })
                );
            }),
            take(1)
        );
    }

    private getRequest(request: Observable<string>): Observable<string> {
        return request
            .pipe(
                switchMap(auth => {
                    if (auth?.startsWith(FORCE_RESET_RESPONSE_PREFIX) || auth === FORCE_RESET_MARKER) {
                        return of(auth);
                    }
                    this.initializeService.setUserName(auth);
                    if (this.initializeService.getService().length > 0) {
                        let initArray = [];
                        if (this._authentication$.value !== auth) {
                            this.initializeService.getService()
                                .forEach(service => service.destroy && service.destroy());
                        }
                        this.initializeService.getService().forEach(service => {
                            initArray.push(service.init());
                        });
                        initArray.map(serviceRequest => serviceRequest.subscribe())
                        this.prlPagesService.loadPages().subscribe();
                    }
                    return of(auth);
                })
            );
    }

    loggedInUser(): string {
        return this._authentication$.value;
    }

    logOut(message?: string): void {
        this.httpClient.get('/rest/login/logout')
            .subscribe()
            .add(() => {
                this.invalidateAuth(message);
            });
    }

    invalidateAuth(message: string): void {
        const allowedUrls = ['registration', 'contact-us', 'force-reset-password'];
        if (!this.loggedInUser() && !this.router.getCurrentNavigation()) {
            let url = this.router.url;
            url = url.substring(url.lastIndexOf('/') + 1);
            if (allowedUrls.includes(url)) {
                return;
            }
        }
        this.preloader.hide();
        this.showNotification(message);
        this._authentication$.next(null);
        this.token = null;
        this.router.navigateByUrl('/login');
        sessionStorage.clear();
        this.dialogRef.closeAll();
    }

    private showNotification(message: string): void {
        if (message) {
            this.notification.notify(this._authentication$, message.trim())
                .pipe(take(1))
                .subscribe();
        }
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    checkOneLoginEnabled(): Observable<boolean> {
        return undefined;
    }

    getUserEmail(): Observable<string> {
        return undefined;
    }

    private handleError(message: string) {
        this.showNotification(message);
        return undefined;
    }

    public isForceResetRequired(): boolean {
        return sessionStorage.getItem(FORCE_RESET_REQUIRED_KEY) === 'true';
    }

    public clearForceResetRequired(): void {
        sessionStorage.removeItem(FORCE_RESET_REQUIRED_KEY);
    }

    private setForceResetRequired(required: boolean): void {
        if (required) {
            sessionStorage.setItem(FORCE_RESET_REQUIRED_KEY, 'true');
        } else {
            sessionStorage.removeItem(FORCE_RESET_REQUIRED_KEY);
        }
    }
}
